import { useState, useMemo } from 'react'
import { Box } from '@mui/material'
import {
  ProductionRequirementRowType,
  SchedulerPackagingRoutesRowType,
  SchedulerTabsProps,
} from './Scheduler.types'
import { useDeclaredTanks, withoutTanks } from './tankRoutes'
import {
  useScheduleProductionRequirements,
  useSchedulerTableHandlers,
} from './Scheduler.data'
import { useSchedulerData } from 'Features/SchedulerView/SchedulerView.data'
import {
  RouteOptions,
  SchedulerProductionRequirementTable,
} from './SchedulerProductionRequirements.muiTable'
import { SchedulerProductionMasterDataTable } from './SchedulerProductionMasterData.muiTable'
import { FONT_SANS, PillToggle, TOKENS } from './SchedulerDesign'
import { TLSearchBox } from './SchedulerFilterSelect'
import { productRatesFromRoutes } from './specificTime'
import {
  RequirementsByProduct,
  WEEK_NUMBERS,
} from './ProductRequirementsCell.view'

// Week buttons matching the design's compact teal/outline pill row.
const WeekSelector = ({
  value,
  onChange,
}: {
  value: number
  onChange: (w: number) => void
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
    {[1, 2, 3, 4, 5, 6, 7, 8].map((w) => {
      const on = w === value
      return (
        <Box
          component="button"
          key={w}
          onClick={() => onChange(w)}
          sx={{
            width: 26,
            height: 26,
            border: '1px solid',
            borderColor: on ? TOKENS.brandTeal : TOKENS.line300,
            background: on ? TOKENS.brandTeal : '#fff',
            color: on ? '#fff' : TOKENS.ink500,
            borderRadius: '2px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: FONT_SANS,
          }}
        >
          {w}
        </Box>
      )
    })}
  </Box>
)

/**
 * Per-week production requirement totals for every product, keyed by
 * productCode with index 0 = week 1.
 *
 * Eight fixed reads, because requirements are stored one Delta folder per week
 * and there is no all-weeks endpoint (`week=None` reads the base folder, whose
 * `quantity` is the empty template). Eight *explicit* calls rather than a loop
 * so the hook count is statically constant — a hook inside `.map` is what the
 * rules-of-hooks lint exists to catch, even when the array length happens to
 * be fixed.
 *
 * `enabled` gates all eight, so the master-data screen costs nothing extra
 * until the planner asks for requirements. React Query then caches them for
 * the session, and the production-requirements tab shares those same cache
 * entries.
 */
const useAllWeekRequirements = (
  schedulerId: string,
  iteration: string,
  enabled: boolean
) => {
  const API = 'production-requirements'
  const w1 = useScheduleProductionRequirements(
    schedulerId,
    API,
    iteration,
    1,
    enabled
  )
  const w2 = useScheduleProductionRequirements(
    schedulerId,
    API,
    iteration,
    2,
    enabled
  )
  const w3 = useScheduleProductionRequirements(
    schedulerId,
    API,
    iteration,
    3,
    enabled
  )
  const w4 = useScheduleProductionRequirements(
    schedulerId,
    API,
    iteration,
    4,
    enabled
  )
  const w5 = useScheduleProductionRequirements(
    schedulerId,
    API,
    iteration,
    5,
    enabled
  )
  const w6 = useScheduleProductionRequirements(
    schedulerId,
    API,
    iteration,
    6,
    enabled
  )
  const w7 = useScheduleProductionRequirements(
    schedulerId,
    API,
    iteration,
    7,
    enabled
  )
  const w8 = useScheduleProductionRequirements(
    schedulerId,
    API,
    iteration,
    8,
    enabled
  )

  const isLoading =
    w1.isLoading ||
    w2.isLoading ||
    w3.isLoading ||
    w4.isLoading ||
    w5.isLoading ||
    w6.isLoading ||
    w7.isLoading ||
    w8.isLoading

  return useMemo(() => {
    const weekRows = [
      w1.data?.data,
      w2.data?.data,
      w3.data?.data,
      w4.data?.data,
      w5.data?.data,
      w6.data?.data,
      w7.data?.data,
      w8.data?.data,
    ]
    const byProduct: RequirementsByProduct = new Map()
    let globalMax = 0
    weekRows.forEach((rows, weekIdx) => {
      for (const row of (rows ?? []) as ProductionRequirementRowType[]) {
        const code = row?.productCode
        if (!code) continue
        const qty = Number(row?.quantity)
        if (!Number.isFinite(qty) || qty <= 0) continue
        const arr =
          byProduct.get(String(code)) ?? Array(WEEK_NUMBERS.length).fill(0)
        // Summed, not assigned: one product can appear on more than one row in
        // a week (different input products), and the requirement is the total.
        arr[weekIdx] += qty
        byProduct.set(String(code), arr)
        if (arr[weekIdx] > globalMax) globalMax = arr[weekIdx]
      }
    })
    return { byProduct, globalMax, isLoading }
  }, [
    w1.data,
    w2.data,
    w3.data,
    w4.data,
    w5.data,
    w6.data,
    w7.data,
    w8.data,
    isLoading,
  ])
}

/** The two numeric carry-over columns arrived later than the rest of the
 *  production-requirements table and reached Delta by schema evolution, so a
 *  week's table can hand them back typed as string ("1200.0") where
 *  `quantity` has been natively numeric since day one. Coerce them once here,
 *  on the way in, so both columns can be declared as the plain
 *  `type: 'number'` + `editable` pair every other editable numeric column in
 *  the app uses — no per-column valueGetter/valueSetter, and therefore the
 *  grid's own default commit path.
 *
 *  A value that is not a number at all is passed through untouched rather
 *  than blanked: that would be data loss, and the cell showing something odd
 *  is the honest signal.
 */
const numericOrRaw = (v: unknown): unknown => {
  if (v === null || v === undefined || v === '') return v
  const n = Number(v)
  return Number.isFinite(n) ? n : v
}

const normalizeCarryOverNumerics = (
  rows: ProductionRequirementRowType[]
): ProductionRequirementRowType[] =>
  rows.map((r) => {
    const qty = numericOrRaw(r?.carryThroughQuantity)
    const hrs = numericOrRaw(r?.carryThroughMaxRunConsumed)
    // Keep the identity when nothing changed — these rows feed React Query
    // consumers and grid memoization.
    if (
      qty === r?.carryThroughQuantity &&
      hrs === r?.carryThroughMaxRunConsumed
    )
      return r
    return {
      ...r,
      carryThroughQuantity: qty as number | null,
      carryThroughMaxRunConsumed: hrs as number | null,
    }
  })

export const ScheduleProductionRequirement = ({
  schedulerId,
  productMasterData = false,
  iteration,
}: SchedulerTabsProps & { productMasterData?: boolean }) => {
  const API = productMasterData
    ? 'product-master-data'
    : 'production-requirements'
  const [selectedWeek, setSelectedWeek] = useState(1)
  const effectiveWeek = productMasterData ? undefined : selectedWeek

  const { data: scheduleData } = useSchedulerData(schedulerId, iteration)
  const showReschedule = useMemo(() => {
    if (productMasterData) return false
    try {
      const parsed = JSON.parse(
        scheduleData?.schedule?.versionDetails ?? '{}'
      ) as Record<string, { defaultToMonday?: boolean }>
      return parsed[iteration]?.defaultToMonday === false
    } catch {
      return false
    }
  }, [scheduleData, iteration, productMasterData])

  const { data: responseData = { data: [], predicate: undefined }, isLoading } =
    useScheduleProductionRequirements(
      schedulerId,
      API,
      iteration,
      effectiveWeek
    )
  const { data: rawData = [], predicate } = responseData
  // Product master data has no carry-over columns, so it passes straight
  // through — the coercion is only about this table's two numeric ones.
  const data = useMemo(
    () =>
      productMasterData
        ? (rawData as ProductionRequirementRowType[])
        : normalizeCarryOverNumerics(rawData as ProductionRequirementRowType[]),
    [rawData, productMasterData]
  )

  const {
    updateTableData,
    uploadScheduleData,
    deleteTableData,
    isSaving,
    isUploading,
    isDeleting,
    updateRowsStatus,
  } = useSchedulerTableHandlers(schedulerId, API, iteration, effectiveWeek)

  // ─── Carry-over (carry-through) ──────────────────────────────────────────
  // Off by default to keep the normal-flow view uncluttered — the same reason
  // the reschedule columns are hidden. Forced on whenever a row already
  // carries a volume: hiding data that exists is worse than the clutter, and
  // that volume changes how the week is scheduled.
  const [carryThroughRequested, setCarryThroughRequested] = useState(false)
  const hasExistingCarryThrough = useMemo(
    () =>
      !productMasterData &&
      (data as ProductionRequirementRowType[]).some((r) => {
        const n = Number(r?.carryThroughQuantity)
        return Number.isFinite(n) && n > 0
      }),
    [data, productMasterData]
  )
  const showCarryThrough =
    !productMasterData && (carryThroughRequested || hasExistingCarryThrough)

  // Packing routes give the valid kitchens/lines per product, so the two
  // allocation dropdowns can only offer routes that actually exist, and the
  // line rates the Specific Time "windows too short" check needs. Fetched on
  // any week's demand grid (not on product master data, which has neither).
  const { data: routesResponse } = useScheduleProductionRequirements(
    schedulerId,
    'packing-routes',
    iteration,
    undefined,
    !productMasterData
  )
  const productRates = useMemo(
    () =>
      productRatesFromRoutes(
        (routesResponse?.data ?? []) as SchedulerPackagingRoutesRowType[]
      ),
    [routesResponse]
  )
  // A declared tank in a Kitchen / Line cell is a vessel, not a unit a
  // carry-over can be allocated to (see tankRoutes.ts).
  const declaredTanks = useDeclaredTanks(
    schedulerId,
    iteration,
    !productMasterData
  )
  const routeOptions: RouteOptions = useMemo(() => {
    const rows = withoutTanks(
      (routesResponse?.data ?? []) as SchedulerPackagingRoutesRowType[],
      declaredTanks
    )
    const acc: Record<string, { kitchens: Set<string>; lines: Set<string> }> =
      {}
    for (const r of rows) {
      if (!r?.productCode) continue
      const entry = (acc[r.productCode] ??= {
        kitchens: new Set(),
        lines: new Set(),
      })
      if (r.kitchen) entry.kitchens.add(r.kitchen)
      // packingLine1..4 are a priority order, not four separate slots — any of
      // them is a line this product can genuinely run on.
      for (const line of [
        r.packingLine1,
        r.packingLine2,
        r.packingLine3,
        r.packingLine4,
      ]) {
        if (line) entry.lines.add(line)
      }
    }
    return Object.fromEntries(
      Object.entries(acc).map(([code, v]) => [
        code,
        {
          kitchens: Array.from(v.kitchens).sort(),
          lines: Array.from(v.lines).sort(),
        },
      ])
    )
  }, [routesResponse, declaredTanks])

  // ─── Production requirements on the master-data grid ─────────────────────
  // Off by default and gated, because it costs eight per-week reads. Same
  // shape as the Carry-over and Format toggles: the expensive side data is
  // fetched only once the planner asks to see it.
  const [showRequirements, setShowRequirements] = useState(false)
  const requirementsEnabled = productMasterData && showRequirements
  const {
    byProduct: requirementsByProduct,
    globalMax: requirementsMax,
    isLoading: requirementsLoading,
  } = useAllWeekRequirements(schedulerId, iteration, requirementsEnabled)

  // Most of a master-data list has no demand at all — on the live tst
  // schedule, 26 of 350 products — so hiding the rest is the difference
  // between a usable screen and a haystack. Only offered while the
  // requirements data is actually loaded, since it is what the filter reads.
  const [withDemandOnly, setWithDemandOnly] = useState(false)

  // ─── Free-text filter ────────────────────────────────────────────────────
  // Matches the identifying columns only — codes, descriptions and format —
  // rather than every field. Searching the whole row would make a term like
  // "1" match a quantity, a week number or a conversion factor, which on a
  // 350-row product list is worse than no filter.
  //
  // `format` is included because it is the field a planner most often needs to
  // find a set of products by ("show me the POUCH products"), and it is only
  // present on the master-data view — an absent field simply never matches.
  const [search, setSearch] = useState('')
  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase()
    const demandFilter = requirementsEnabled && withDemandOnly
    if (!q && !demandFilter) return data
    return (data as ProductionRequirementRowType[]).filter((r) => {
      if (demandFilter) {
        const weeks = requirementsByProduct.get(String(r?.productCode))
        if (!weeks || weeks.every((v) => v <= 0)) return false
      }
      if (!q) return true
      return [
        r?.productCode,
        r?.productName,
        (r as unknown as Record<string, unknown>)?.inputProductCode,
        (r as unknown as Record<string, unknown>)?.inputProductName,
        r?.format,
      ].some((v) => v != null && String(v).toLowerCase().includes(q))
    })
  }, [data, search, requirementsEnabled, withDemandOnly, requirementsByProduct])

  return (
    <Box
      sx={{
        height: 'calc(100vh - 240px)',
        minHeight: 400,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        {!productMasterData && (
          <>
            <WeekSelector value={selectedWeek} onChange={setSelectedWeek} />
            <Box sx={{ mb: 1.5 }}>
              <PillToggle
                label="Carry-over"
                on={showCarryThrough}
                onClick={() => setCarryThroughRequested((v) => !v)}
                title={
                  hasExistingCarryThrough
                    ? 'This week already has carry-over volume, so these columns stay visible.'
                    : 'Show the carry-over columns: volume already running on a line at hour 0 of this week.'
                }
              />
            </Box>
          </>
        )}
        {productMasterData && (
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PillToggle
              label="Requirements"
              on={showRequirements}
              onClick={() => setShowRequirements((v) => !v)}
              title="Show each product's production requirement across weeks 1–8. Reads the eight weekly requirement tables, so it is off until asked for."
            />
            {requirementsEnabled && (
              <PillToggle
                label="With demand only"
                on={withDemandOnly}
                onClick={() => setWithDemandOnly((v) => !v)}
                title="Hide products with no production requirement in any week — usually most of the list."
              />
            )}
          </Box>
        )}
        {/* Same control as the factory schedule's toolbar, so the filter
            behaves identically on both surfaces. */}
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TLSearchBox
            value={search}
            onChange={setSearch}
            placeholder={
              productMasterData
                ? 'Filter product, description, format…'
                : 'Filter product, description…'
            }
            width={220}
          />
          {search.trim() && (
            // Row counts, because a filter that hides rows silently reads as
            // data loss — especially on a paginated grid where the tail is
            // off-screen anyway.
            <Box
              sx={{
                fontSize: 10.5,
                fontFamily: FONT_SANS,
                color: TOKENS.ink400,
                whiteSpace: 'nowrap',
              }}
            >
              {filteredData.length} of {data.length} shown
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {productMasterData ? (
          <SchedulerProductionMasterDataTable
            data={filteredData}
            unfilteredData={data}
            requirementsByProduct={
              requirementsEnabled ? requirementsByProduct : undefined
            }
            requirementsMax={requirementsMax}
            requirementsLoading={requirementsLoading}
            isLoading={isLoading}
            updateTableData={updateTableData}
            uploadScheduleData={uploadScheduleData}
            isUploading={isUploading}
            isSaving={isSaving}
            updateRowsStatus={updateRowsStatus}
            predicate={predicate}
            deleteTableData={deleteTableData}
            isDeleting={isDeleting}
          />
        ) : (
          <SchedulerProductionRequirementTable
            data={filteredData}
            unfilteredData={data}
            isLoading={isLoading}
            updateTableData={updateTableData}
            uploadScheduleData={uploadScheduleData}
            isUploading={isUploading}
            isSaving={isSaving}
            updateRowsStatus={updateRowsStatus}
            predicate={predicate}
            deleteTableData={deleteTableData}
            isDeleting={isDeleting}
            showRescheduleColumns={showReschedule}
            showCarryThroughColumns={showCarryThrough}
            routeOptions={routeOptions}
            showSpecificTime={!productMasterData}
            productRates={productRates}
          />
        )}
      </Box>
    </Box>
  )
}
